<template>
  <div class="legal-ver">
    <APageHead :title="docName" :sub="documentId" back @back="goList">
      <template #actions>
        <ABtn
          v-if="doc && doc.currentVersion"
          kind="ghost"
          icon="visibility"
          @click="openViewer(doc.currentVersion)"
        >현행 본문 보기</ABtn>
        <ABtn kind="primary" icon="add" @click="goEditor()">새 버전 만들기</ABtn>
      </template>
    </APageHead>

    <!-- 발행해도 activeYn='N' 이면 앱에 안 보입니다. 가장 흔한 운영 실수라 최상단에 띄웁니다. -->
    <div v-if="doc && doc.activeYn === 'N'" class="legal-ver__banner">
      <i class="material-icons-round">visibility_off</i>
      <div>
        <b>이 문서는 앱에 노출되지 않습니다 (미노출 상태).</b>
        <span>본문을 발행해도 앱에 보이지 않고 동의도 수집되지 않습니다. 발행 후 문서 목록에서 노출을 켜세요.</span>
      </div>
      <ABtn size="sm" kind="ghost" @click="goList">문서 목록으로</ABtn>
    </div>

    <!-- 문서 요약 -->
    <ACard v-if="doc" title="문서 정보">
      <div class="legal-ver__summary">
        <div class="legal-ver__pills">
          <APill kind="info">{{ docTypeLabel(doc.docType) }}</APill>
          <APill :kind="doc.isRequired ? 'dark' : 'default'">{{ doc.isRequired ? '필수' : '선택' }}</APill>
          <APill kind="default">대상: {{ targetUserTypeLabel(doc.targetUserType) }}</APill>
          <APill :kind="doc.activeYn === 'Y' ? 'ok' : 'err'" dot>
            {{ doc.activeYn === 'Y' ? '노출 중' : '미노출' }}
          </APill>
        </div>
        <div class="legal-ver__facts">
          <div class="legal-ver__fact">
            <span class="legal-ver__fact-label">현행 버전</span>
            <span v-if="doc.currentVersion" class="legal-ver__fact-value legal-ver__mono">
              v{{ doc.currentVersion }}
            </span>
            <APill v-else kind="err" dot>본문 등록 필요</APill>
          </div>
          <div class="legal-ver__fact">
            <span class="legal-ver__fact-label">현행 시행일 (KST)</span>
            <span class="legal-ver__fact-value">{{ fmtKst(doc.currentEffectiveAt) }}</span>
          </div>
          <div v-if="scheduled" class="legal-ver__fact">
            <span class="legal-ver__fact-label">예약 발행</span>
            <APill kind="info">
              v{{ scheduled.version }} — {{ fmtKst(scheduled.effectiveAt) }} 시행 예정
            </APill>
          </div>
        </div>
      </div>
    </ACard>

    <!-- 버전 이력 -->
    <ACard>
      <template #title>
        <span class="legal-ver__sec-title">
          <i class="material-icons-round legal-ver__sec-icon">history_edu</i>
          버전 이력
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ versions.length }}건</APill>
      </template>

      <div v-if="loading" class="legal-ver__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>버전 이력을 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="!versions.length"
        title="등록된 버전이 없습니다"
        sub="'새 버전 만들기'로 약관 본문을 등록하세요. 발행하기 전까지는 앱에 노출되지 않습니다."
      />

      <ATable v-else :columns="columns" :rows="versions" row-key="idx" striped bordered>
        <template #cell:version="{ row }">
          <span class="legal-ver__mono">v{{ row.version }}</span>
        </template>

        <template #cell:status="{ row }">
          <APill :kind="statusOf(row).kind" :dot="statusOf(row).dot">{{ statusOf(row).label }}</APill>
        </template>

        <!-- 실질 개정이라도 차단형 재동의가 걸리는 건 필수 문서뿐이라, 색으로 심각도를 구분합니다. -->
        <template #cell:requiresReconsent="{ row }">
          <APill :kind="row.requiresReconsent ? (blocksUsers ? 'err' : 'warn') : 'default'">
            {{ row.requiresReconsent ? '실질 개정' : '경미 개정' }}
          </APill>
        </template>

        <template #cell:langs="{ row }">
          <div class="legal-ver__langs">
            <span v-for="l in row.langs" :key="l" class="legal-ver__lang">{{ l }}</span>
            <APill v-if="doc && doc.isRequired && !(row.langs || []).includes('ko')" kind="err">
              ko 미등록
            </APill>
            <APill v-else-if="!(row.langs || []).includes('en')" kind="default">en 미등록</APill>
          </div>
        </template>

        <template #cell:effectiveAt="{ row }">
          <span class="legal-ver__date">{{ fmtKst(row.effectiveAt) }}</span>
        </template>

        <template #cell:publishedAt="{ row }">
          <span class="legal-ver__date">{{ fmtKst(row.publishedAt) }}</span>
        </template>

        <template #cell:createdBy="{ row }">
          <span class="legal-ver__by" :title="row.createdBy">{{ row.createdBy || '-' }}</span>
        </template>

        <template #cell:_actions="{ row }">
          <div class="legal-ver__actions">
            <ABtn size="sm" kind="ghost" icon="visibility" icon-only title="본문 보기" @click="openViewer(row.version)" />

            <!-- draft 만 수정·삭제·발행. 발행본은 서버가 409로 막으므로 버튼 자체를 내지 않습니다. -->
            <template v-if="row.status === 'draft'">
              <ABtn size="sm" kind="ghost" icon="edit" icon-only title="초안 수정" @click="goEditor({ version: row.version })" />
              <ABtn size="sm" kind="danger" icon="delete" icon-only title="초안 삭제" @click="removeDraft(row)" />
              <ABtn size="sm" kind="primary" icon="publish" @click="openPublish(row)">발행</ABtn>
            </template>

            <!-- 발행본은 "수정" 대신 "새 버전" 이 정답입니다 (개정 = 새 버전 발행) -->
            <template v-else-if="row.status === 'published'">
              <ABtn size="sm" kind="ghost" icon="add" @click="goEditor({ copyFrom: row.version })">
                새 버전
              </ABtn>
              <span :title="archiveBlockedReason(row)">
                <ABtn
                  size="sm"
                  kind="ghost"
                  icon="inventory_2"
                  icon-only
                  :disabled="!!archiveBlockedReason(row)"
                  @click="archive(row)"
                />
              </span>
            </template>
          </div>
        </template>
      </ATable>
    </ACard>

    <LegalVersionPublishModal
      v-model="showPublish"
      :document-id="documentId"
      :document-name="docName"
      :version="publishTarget.version || ''"
      :requires-reconsent="!!publishTarget.requiresReconsent"
      :is-required-doc="!!(doc && doc.isRequired)"
      :doc-type="doc ? doc.docType || '' : ''"
      :version-langs="publishTarget.langs || []"
      :published-effective-ats="publishedEffectiveAts"
      :current-effective-at="doc ? doc.currentEffectiveAt || '' : ''"
      @published="reload"
    />

    <LegalVersionViewerModal
      v-model="showViewer"
      :document-id="documentId"
      :version="viewerVersion"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import LegalVersionPublishModal from "./components/LegalVersionPublishModal.vue";
import LegalVersionViewerModal from "./components/LegalVersionViewerModal.vue";
import { apiErrorMessage } from "@/utils/apiError";
import {
  fmtKst,
  isFuture,
  docTypeLabel,
  targetUserTypeLabel,
  VERSION_STATUS,
} from "@/utils/legal";

export default {
  name: "SystemLegalDocumentDetail",
  components: {
    APageHead,
    ACard,
    ABtn,
    APill,
    ATable,
    AEmpty,
    LegalVersionPublishModal,
    LegalVersionViewerModal,
  },
  props: {
    documentId: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      doc: null,
      versions: [],
      showPublish: false,
      publishTarget: {},
      showViewer: false,
      viewerVersion: "",
      columns: [
        { key: "version", label: "버전", width: 80, align: "center" },
        { key: "status", label: "상태", width: 150, align: "center" },
        { key: "requiresReconsent", label: "개정 성격", width: 105, align: "center" },
        { key: "langs", label: "본문 언어", width: 160 },
        { key: "effectiveAt", label: "시행일 (KST)", width: 135 },
        { key: "publishedAt", label: "발행일 (KST)", width: 135 },
        { key: "createdBy", label: "작성자", width: 180 },
        { key: "_actions", label: "관리", align: "right", width: 230 },
      ],
    };
  },
  computed: {
    docName() {
      return this.doc?.names?.ko || this.doc?.names?.en || "약관 문서";
    },
    // 발행됐지만 시행일이 아직 오지 않은 버전 (사전 고지 중)
    scheduled() {
      return this.versions.find((v) => v.status === "published" && isFuture(v.effectiveAt)) || null;
    },
    publishedEffectiveAts() {
      return this.versions
        .filter((v) => v.status === "published" && v.effectiveAt)
        .map((v) => v.effectiveAt);
    },
    publishedCount() {
      return this.versions.filter((v) => v.status === "published").length;
    },
    // 차단형 재동의 화면은 필수 문서에만 걸립니다 (선택 동의는 강제 불가, NDA 는 강사 웹 전용).
    blocksUsers() {
      return !!this.doc?.isRequired && this.doc?.docType !== "nda";
    },
  },
  created() {
    this.reload();
  },
  methods: {
    fmtKst,
    docTypeLabel,
    targetUserTypeLabel,
    goList() {
      this.$router.push({ name: "SystemLegalDocuments" });
    },
    goEditor(query = {}) {
      this.$router.push({
        name: "SystemLegalVersionEditor",
        params: { documentId: this.documentId },
        query,
      });
    },
    openViewer(version) {
      this.viewerVersion = String(version);
      this.showViewer = true;
    },
    openPublish(row) {
      this.publishTarget = row;
      this.showPublish = true;
    },
    /**
     * 상태 뱃지. published 안에서도 "현행 / 시행 예정 / 지난 발행본"을 구분해야
     * 사전 고지 발행 후 현행이 안 바뀌는 동작을 화면에서 이해할 수 있습니다.
     */
    statusOf(row) {
      if (row.status !== "published") {
        const meta = VERSION_STATUS[row.status] || { label: row.status, pill: "default" };
        return { kind: meta.pill, label: meta.label, dot: false };
      }
      if (isFuture(row.effectiveAt)) {
        return {
          kind: "info",
          label: `시행 예정 ${fmtKst(row.effectiveAt, { withTime: false })}`,
          dot: false,
        };
      }
      if (this.doc && String(row.version) === String(this.doc.currentVersion)) {
        return { kind: "ok", label: "현행", dot: true };
      }
      return { kind: "default", label: "지난 발행본", dot: false };
    },
    // 필수 문서의 마지막 발행본은 서버가 409로 막습니다. 버튼을 미리 비활성화하고 사유를 툴팁에 답니다.
    archiveBlockedReason(row) {
      if (row.status !== "published") return "발행본만 보관할 수 있습니다";
      if (this.doc?.isRequired && this.publishedCount <= 1) {
        return "필수 문서의 마지막 발행 버전은 보관할 수 없습니다. 새 버전을 먼저 발행하세요.";
      }
      return "";
    },
    async reload() {
      this.loading = true;
      try {
        // 문서 마스터 단건 조회 API가 없어 목록에서 찾습니다.
        const [docsRes, versionsRes] = await Promise.all([
          this.$axios({ method: "get", url: "/emp/legal/documents", loadingBar: true }),
          this.$axios({
            method: "get",
            url: `/emp/legal/documents/${encodeURIComponent(this.documentId)}/versions`,
          }),
        ]);
        this.doc = (docsRes.data || []).find((d) => d.documentId === this.documentId) || null;
        this.versions = [...(versionsRes.data || [])].sort((a, b) => (b.idx || 0) - (a.idx || 0));

        if (!this.doc) {
          await swal.fire("오류", "문서를 찾을 수 없습니다.", "error");
          this.goList();
        }
      } catch (e) {
        console.error("버전 이력 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "버전 이력을 불러오지 못했습니다."), "error");
      } finally {
        this.loading = false;
      }
    },
    async removeDraft(row) {
      const ok = await swal.fire({
        icon: "warning",
        title: "초안 삭제",
        text: `v${row.version} 초안을 삭제하시겠습니까? 작성한 본문이 사라집니다.`,
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        confirmButtonColor: "#d33",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "delete",
          url: `/emp/legal/documents/${encodeURIComponent(this.documentId)}/versions/${encodeURIComponent(row.version)}`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: `v${row.version} 초안을 삭제했습니다.` });
      } catch (e) {
        console.error("초안 삭제 실패:", e);
        swal.fire("삭제 실패", apiErrorMessage(e, "초안을 삭제하지 못했습니다."), "error");
      } finally {
        this.reload();
      }
    },
    async archive(row) {
      if (this.archiveBlockedReason(row)) return;

      const ok = await swal.fire({
        icon: "warning",
        title: "버전 보관",
        text: `v${row.version} 을 보관 처리합니다. 본문과 동의 이력은 그대로 남고 목록에서만 정리됩니다.`,
        showCancelButton: true,
        confirmButtonText: "보관",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "patch",
          url: `/emp/legal/documents/${encodeURIComponent(this.documentId)}/versions/${encodeURIComponent(row.version)}/archive`,
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: `v${row.version} 을 보관했습니다.` });
      } catch (e) {
        console.error("archive 실패:", e);
        swal.fire("보관 실패", apiErrorMessage(e, "보관하지 못했습니다."), "error");
      } finally {
        this.reload();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.legal-ver {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    background: $p-err-soft;
    border: 1px solid rgba($p-err, 0.3);
    border-radius: $p-radius-md;

    > i {
      font-size: 20px !important;
      color: $p-err;
    }

    > div {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;

      b { font-size: 12px; font-weight: 800; color: $p-err-ink; }
      span { font-size: 11px; line-height: 1.5; color: $p-ink-mid; }
    }
  }

  &__summary {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__pills {
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
  }

  &__facts {
    display: flex;
    gap: 32px;
    flex-wrap: wrap;
    padding-top: 12px;
    border-top: 1px solid $p-line-soft;
  }

  &__fact {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  &__fact-label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $p-ink-soft;
  }

  &__fact-value {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__mono {
    font-family: $p-font-mono;
    font-weight: 700;
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__langs {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
  }

  &__lang {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 5px;
    background: $p-line-soft;
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 700;
    color: $p-ink-mid;
    text-transform: uppercase;
  }

  &__date {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-mid;
  }

  &__by {
    display: inline-block;
    max-width: 170px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: $p-ink-soft;
    vertical-align: middle;
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
}
</style>
